import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  message,
  Spin,
  Typography,
  Tooltip,
  Statistic,
  Row,
  Col,
  Modal,
  DatePicker,
  Grid,
} from 'antd';
import {
  SyncOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import adsService, { AdAccount } from '../../services/ads.service';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const AdAccounts: React.FC = () => {
  const [accounts, setAccounts] = useState<AdAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingAll, setSyncingAll] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedAccountForSync, setSelectedAccountForSync] = useState<string | null>(null);
  const [syncDateRange, setSyncDateRange] = useState<[any, any] | null>(null);

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const data = await adsService.getAdAccounts();
      setAccounts(data);
    } catch (error) {
      message.error('Không thể tải danh sách tài khoản quảng cáo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleSyncAccounts = async () => {
    setSyncing(true);
    try {
      const result = await adsService.syncAdAccounts();
      message.success(`Đã đồng bộ ${result.count} tài khoản quảng cáo`);
      setAccounts(result.data);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Đồng bộ thất bại');
    } finally {
      setSyncing(false);
    }
  };

  const formatCurrency = (value: number, currency: string = 'VND') => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
    }).format(value / 100); // Facebook returns in minor units
  };

  const columns: ColumnsType<AdAccount> = [
    {
      title: 'Tài khoản',
      dataIndex: 'name',
      key: 'name',
      width: isMobile ? 180 : 250,
      render: (text, record) => (
        <div>
          <Text strong style={{ fontSize: isMobile ? 13 : 14 }}>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>
            ID: {record.accountId}
          </Text>
        </div>
      ),
    },
    {
      title: 'Tiền tệ',
      dataIndex: 'currency',
      key: 'currency',
      width: 90,
      align: 'center',
      render: (currency) => <Tag color="blue" style={{ margin: 0, fontSize: isMobile ? 11 : 12 }}>{currency}</Tag>,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      align: 'center',
      render: (isActive) =>
        isActive ? (
          <Tag color="success" icon={<CheckCircleOutlined />} style={{ margin: 0, fontSize: isMobile ? 11 : 12 }}>
            Hoạt động
          </Tag>
        ) : (
          <Tag color="default" icon={<PauseCircleOutlined />} style={{ margin: 0, fontSize: isMobile ? 11 : 12 }}>
            Tạm dừng
          </Tag>
        ),
    },
    {
      title: 'Đồng bộ cuối',
      dataIndex: 'lastSyncedAt',
      key: 'lastSyncedAt',
      width: 150,
      render: (date) =>
        date ? (
          <Tooltip title={new Date(date).toLocaleString('vi-VN')}>
            <Space size={4} style={{ fontSize: isMobile ? 12 : 14 }}>
              <ClockCircleOutlined />
              {new Date(date).toLocaleDateString('vi-VN')}
            </Space>
          </Tooltip>
        ) : (
          <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>Chưa đồng bộ</Text>
        ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 110,
      align: 'center',
      fixed: (isMobile ? 'right' : undefined) as any,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<SyncOutlined spin={syncingAll === record._id} />}
          loading={syncingAll === record._id}
          onClick={() => {
            setSelectedAccountForSync(record._id);
            setIsModalVisible(true);
          }}
        >
          {isMobile ? '' : 'Đồng bộ'}
        </Button>
      ),
    },
  ];

  const handleModalSync = async () => {
    if (!selectedAccountForSync || !syncDateRange) {
      message.warning('Vui lòng chọn khoảng ngày đồng bộ');
      return;
    }

    const [start, end] = syncDateRange;
    const startDate = start.format('YYYY-MM-DD');
    const endDate = end.format('YYYY-MM-DD');

    setSyncingAll(selectedAccountForSync);
    setIsModalVisible(false);
    
    try {
      const result = await adsService.syncAll(selectedAccountForSync, startDate, endDate);
      message.success(
        `Đồng bộ hoàn tất: ${result.campaigns} chiến dịch, ${result.adSets} nhóm QC, ${result.ads} QC, ${result.insights} insights`
      );
      fetchAccounts();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Đồng bộ thất bại');
    } finally {
      setSyncingAll(null);
      setSyncDateRange(null);
      setSelectedAccountForSync(null);
    }
  };

  return (
    <div style={{ padding: isMobile ? '12px' : '24px' }}>
      <Card
        title={
          <Space size={isMobile ? 8 : 12}>
            <DollarOutlined style={{ fontSize: isMobile ? 20 : 24, color: '#1890ff' }} />
            <Title level={isMobile ? 5 : 4} style={{ margin: 0 }}>
              {isMobile ? 'Tài khoản QC' : 'Tài khoản quảng cáo'}
            </Title>
          </Space>
        }
        extra={
          <Button
            type="primary"
            size={isMobile ? 'small' : 'middle'}
            icon={<SyncOutlined spin={syncing} />}
            loading={syncing}
            onClick={handleSyncAccounts}
          >
            {isMobile ? 'Đồng bộ' : 'Đồng bộ tài khoản'}
          </Button>
        }
        bodyStyle={{ padding: isMobile ? '12px' : '24px' }}
      >
        {/* Summary Stats */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card size="small" bodyStyle={{ padding: isMobile ? '8px 12px' : '12px' }}>
              <Statistic
                title="Tổng tài khoản"
                value={accounts.length}
                valueStyle={{ color: '#1890ff', fontSize: isMobile ? 20 : 24 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small" bodyStyle={{ padding: isMobile ? '8px 12px' : '12px' }}>
              <Statistic
                title="Đang hoạt động"
                value={accounts.filter((a) => a.isActive).length}
                valueStyle={{ color: '#52c41a', fontSize: isMobile ? 20 : 24 }}
              />
            </Card>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={accounts}
          rowKey="_id"
          loading={loading}
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'Chưa có tài khoản. Nhấn "Đồng bộ" để bắt đầu.' }}
        />
      </Card>

      <Modal
        title="Khoảng ngày đồng bộ"
        open={isModalVisible}
        onOk={handleModalSync}
        onCancel={() => {
          setIsModalVisible(false);
          setSyncDateRange(null);
          setSelectedAccountForSync(null);
        }}
        confirmLoading={syncingAll !== null}
        okText="Đồng bộ ngay"
        cancelText="Hủy"
        width={isMobile ? '95%' : 500}
      >
        <div style={{ padding: '10px 0' }}>
          <Text style={{ display: 'block', marginBottom: 16 }}>
            Đồng bộ Chiến dịch, Nhóm QC, Quảng cáo và Chỉ số (Insights) trong khoảng ngày:
          </Text>
          <DatePicker.RangePicker
            style={{ width: '100%' }}
            onChange={(dates) => setSyncDateRange(dates as [any, any])}
            value={syncDateRange}
            placeholder={['Từ ngày', 'Đến ngày']}
          />
        </div>
      </Modal>
    </div>
  );
};

export default AdAccounts;
